'use client'

import { AllPurposeLink } from '@components/all-purpose-link'
import { buildTweetUrl, createFilterQueryString, formatDate } from '@utils/common'
import { trackButtonClicked, TrackedButtonNames } from '@utils/analytics'
import { journalRoute } from '@utils/routes'
import {
  BodyText,
  DetailText,
  DisplayText,
  ExportIcon,
  HeadlineText,
  LinkIcon,
  XLogoIcon
} from 'design-configuration/components'
import React from 'react'
import { ArticleCollectionsDisplay } from '../article-collections-display'
import { EmbedUrlDisplay } from '../embed-url-display'
import { HTMLContentDisplay } from '../html-content-display'
import { MoreArticlesSection } from '../more-articles-section'
import { ArticleDisplayProps } from '../types'

export const ArticleDisplay = ({
  individualArticleData,
  moreArticlesData
}: ArticleDisplayProps): React.ReactElement => {
  const [showShareMenu, setShowShareMenu] = React.useState(false)
  const article = individualArticleData?.articles?.data?.[0]?.attributes

  return (
    <div className="pt-nav-bar-height">
      {/* header */}
      <div
        className="relative aspect-[3/2] h-auto w-full sm:aspect-auto sm:h-[481px]"
        style={{
          background: `linear-gradient(0deg, rgb(var(--primary-40)) 0%, rgb(var(--primary-40)) 100%), url(${article?.coverPhoto?.data?.attributes?.url}) lightgray 50% / cover no-repeat`,
          boxShadow: '0px -26px 39.1px 0px rgba(0, 0, 0, 0.25) inset'
        }}
      >
        <div
          className="sm:p-2xl p-md absolute bottom-0 z-20 flex w-full items-end justify-between"
          style={{
            background:
              'linear-gradient(0deg, rgba(30, 30, 30, 0.55) 0%, rgba(30, 30, 30, 0.40) 27.85%, rgba(30, 30, 30, 0.19) 66.79%, rgba(30, 30, 30, 0.00) 100%)'
          }}
        >
          <div className="text-surface gap-y-xs flex flex-col md:pr-[80px]">
            {article?.category ? (
              <AllPurposeLink
                href={journalRoute(`?${createFilterQueryString('categoryFilterValue', article?.category)}`)}
              >
                <DetailText className="text-on-surface-inverted font-medium">
                  {article?.category?.toUpperCase()}
                </DetailText>
              </AllPurposeLink>
            ) : null}
            <DisplayText className="hidden text-left font-medium sm:block">{article?.title}</DisplayText>
            <HeadlineText className="text-left font-medium sm:hidden">{article?.title}</HeadlineText>
            <div className="gap-x-xs flex">
              {article?.author ? <BodyText>by {article?.author}</BodyText> : null}
              {article?.author ? <BodyText>•</BodyText> : null}
              <BodyText>
                {formatDate(
                  new Date(article?.originallyPublished ?? article?.publishedAt),
                  {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  },
                  'en-UK'
                )}
              </BodyText>
            </div>
          </div>
          <div className="relative">
            {showShareMenu ? (
              <>
                {/* thing behind share menu to capture touches outside */}
                <div className="fixed left-0 top-0 h-full w-full" onClick={() => setShowShareMenu(false)}></div>
                <div className="bottom-xl p-xs border-outline-variant bg-surface absolute right-0 flex w-[136px] flex-col rounded-[6px] border">
                  <AllPurposeLink
                    className="p-xs hover:bg-surface-container rounded-xs gap-x-xs flex flex-row items-center"
                    href={buildTweetUrl(
                      `Check out ${article?.title ?? 'this article'} from @artblocks_io`,
                      `${window.location.href}?utm_source=twitter&utm_medium=social&utm_campaign=article_share_button`
                    )}
                    onClick={() => {
                      trackButtonClicked(TrackedButtonNames.articleShareButton, {
                        article: article ?? undefined,
                        customProperties: {
                          Medium: 'Twitter'
                        }
                      })
                      setShowShareMenu(false)
                    }}
                  >
                    <XLogoIcon className="fill-on-surface h-[14px] w-[14px]" />
                    <BodyText>Share on X</BodyText>
                  </AllPurposeLink>
                  <button
                    className="p-xs hover:bg-surface-container rounded-xs gap-x-xs flex flex-row items-center"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `${window.location.href}?utm_source=copy_link&utm_medium=social&utm_campaign=article_share_button`
                      )
                      trackButtonClicked(TrackedButtonNames.articleShareButton, {
                        article: article ?? undefined,
                        customProperties: {
                          Medium: 'Copy link'
                        }
                      })
                      setShowShareMenu(false)
                    }}
                  >
                    <LinkIcon className="fill-on-surface h-[14px] w-[14px]" />
                    <BodyText>Copy link</BodyText>
                  </button>
                </div>
              </>
            ) : null}
            <button
              className="bg-primary-inverted-10 p-xs h-lg w-lg relative z-40 cursor-pointer rounded-[64px]"
              onClick={() => setShowShareMenu(!showShareMenu)}
            >
              <ExportIcon className="fill-surface" />
            </button>
          </div>
        </div>
      </div>
      <div className="bg-surface relative flex flex-col items-center">
        <div className="pt-2xl relative flex w-full flex-col items-center sm:pt-[80px]">
          <div className="px-md">
            {article?.embedURL ? (
              <div className="w-full max-w-[850px]">
                <EmbedUrlDisplay
                  url={article?.embedURL}
                  title={`${article?.title} embedded url`}
                  category={article?.category}
                />
              </div>
            ) : null}
            {article?.content ? (
              <div className="w-full max-w-[850px]">
                <HTMLContentDisplay rawContent={article.content} />
              </div>
            ) : null}
          </div>
          {article?.CollectionsSection?.length ? (
            <div className="px-md w-full">
              <hr className="my-2xl border-outline-variant w-full" />
              <HeadlineText className="mb-md font-light">Explore collections</HeadlineText>
              <div className="md:gap-md gap-xl grid md:grid-cols-3">
                {article.CollectionsSection.map((project) => {
                  return <ArticleCollectionsDisplay key={project?.projectId} project={project?.Project} />
                })}
              </div>
            </div>
          ) : null}
          <div className="mt-2xl relative w-full sm:mt-[80px]">
            <MoreArticlesSection moreArticlesData={moreArticlesData} />
          </div>
        </div>
      </div>
    </div>
  )
}
